import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  LocalShipping as ShipmentIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  DirectionsCar as CarIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSnackbar } from 'notistack';
import StatCard from '../components/dashboard/StatCard';
import RevenueChart from '../components/dashboard/RevenueChart';
import StatusDistributionChart from '../components/dashboard/StatusDistributionChart';
import DriversOverview from '../components/dashboard/DriversOverview';
import RecentActivities from '../components/dashboard/RecentActivities';
import dashboardService, {
  DashboardStats,
  DriverStats,
  RevenueData,
  DriverOverview,
  ShipmentStatusDistribution
} from '../services/dashboardService';
import { formatXOF } from '../utils/currency';

const WelcomeCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '200px',
    height: '200px',
    background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
    transform: 'translate(50%, -50%)',
  },
}));

const QuickActionCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.customShadows.z16,
    borderColor: theme.palette.primary.main,
  },
}));

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Admin dashboard state
  const [adminStats, setAdminStats] = useState<DashboardStats | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [driversOverview, setDriversOverview] = useState<DriverOverview[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<ShipmentStatusDistribution[]>([]);
  
  // Driver dashboard state
  const [driverStats, setDriverStats] = useState<DriverStats | null>(null);
  
  // Common state
  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  const isAdmin = user?.role === 'admin';
  const isDriver = user?.role === 'driver';

  useEffect(() => {
    loadDashboardData();
  }, [user?.role]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isAdmin) {
        // Load admin dashboard data
        const [stats, revenue, drivers, distribution, activities] = await Promise.all([
          dashboardService.getAdminStats(),
          dashboardService.getRevenueData('month'),
          dashboardService.getDriversOverview(),
          dashboardService.getShipmentStatusDistribution(),
          dashboardService.getRecentActivities(8)
        ]);

        setAdminStats(stats);
        setRevenueData(revenue);
        setDriversOverview(drivers);
        setStatusDistribution(distribution);
        setRecentActivities(activities);
      } else if (isDriver) {
        // Load driver dashboard data
        const [stats, activities] = await Promise.all([
          dashboardService.getDriverStats(),
          dashboardService.getRecentActivities(5)
        ]);

        setDriverStats(stats);
        setRecentActivities(activities);
      }
    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      setError(error.message || 'Failed to load dashboard data');
      enqueueSnackbar(t('dashboard.error'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 18) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={loadDashboardData}>
              {t('dashboard.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Welcome Section */}
      <WelcomeCard>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {getGreeting()}, {user?.name}! 👋
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                {isAdmin 
                  ? t('dashboard.overview') 
                  : t('dashboard.yourRecentActivities')
                }
              </Typography>
            </Box>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: alpha(theme.palette.common.white, 0.2),
                color: 'inherit',
                fontSize: '2rem',
                fontWeight: 700,
              }}
            >
              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
            </Avatar>
          </Box>
        </CardContent>
      </WelcomeCard>

      {/* Admin Dashboard */}
      {isAdmin && adminStats && (
        <>
          {/* Key Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('dashboard.totalShipments')}
                value={adminStats.totalShipments}
                icon={ShipmentIcon}
                color="primary"
                trend={{
                  direction: 'up',
                  value: '+12%',
                  label: 'vs last month'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('dashboard.activeDrivers')}
                value={adminStats.activeDrivers}
                icon={PeopleIcon}
                color="info"
                trend={{
                  direction: 'up',
                  value: '+3',
                  label: 'this week'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('dashboard.monthlyRevenue')}
                value={formatXOF(adminStats.totalRevenue)}
                icon={MoneyIcon}
                color="success"
                trend={{
                  direction: 'up',
                  value: '+18%',
                  label: 'vs last month'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('dashboard.pendingShipments')}
                value={adminStats.pendingShipments}
                icon={TrendingUpIcon}
                color="warning"
                subtitle={`${adminStats.completedShipments} completed today`}
              />
            </Grid>
          </Grid>

          {/* Charts and Analytics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} lg={8}>
              <RevenueChart 
                data={revenueData} 
                title={t('dashboard.revenueChart')}
                height={350}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <StatusDistributionChart 
                data={statusDistribution}
                height={350}
              />
            </Grid>
          </Grid>

          {/* Drivers and Activities */}
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <DriversOverview drivers={driversOverview} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <RecentActivities activities={recentActivities} />
            </Grid>
          </Grid>
        </>
      )}

      {/* Driver Dashboard */}
      {isDriver && driverStats && (
        <>
          {/* Driver Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('dashboard.totalDeliveries')}
                value={driverStats.totalDeliveries}
                icon={CheckCircleIcon}
                color="success"
                subtitle="All time"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('dashboard.todaysDeliveries')}
                value={driverStats.completedToday}
                icon={SpeedIcon}
                color="primary"
                trend={{
                  direction: 'up',
                  value: '+2',
                  label: t('dashboard.vsYesterday')
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('dashboard.currentStatus')}
                value={driverStats.currentStatus.charAt(0).toUpperCase() + driverStats.currentStatus.slice(1)}
                icon={CarIcon}
                color={driverStats.currentStatus === 'available' ? 'success' : driverStats.currentStatus === 'busy' ? 'warning' : 'error'}
                subtitle={t('dashboard.tapToChange')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('dashboard.rating')}
                value={`${driverStats.rating.toFixed(1)} ⭐`}
                icon={StarIcon}
                color="warning"
                subtitle={t('dashboard.customerRating')}
              />
            </Grid>
          </Grid>

          {/* Driver Earnings */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                title={t('dashboard.todaysEarnings')}
                value={formatXOF(driverStats.earnings.today)}
                icon={MoneyIcon}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                title={t('dashboard.thisWeek')}
                value={formatXOF(driverStats.earnings.thisWeek)}
                icon={MoneyIcon}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                title={t('dashboard.thisMonth')}
                value={formatXOF(driverStats.earnings.thisMonth)}
                icon={MoneyIcon}
                color="primary"
              />
            </Grid>
          </Grid>

          {/* Quick Actions for Drivers */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <QuickActionCard>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <AssignmentIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('dashboard.activeShipments')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {driverStats.activeShipments} {t('dashboard.shipmentsAssigned')}
                  </Typography>
                  <Button variant="contained" size="large">
                    {t('dashboard.viewAssignments')}
                  </Button>
                </CardContent>
              </QuickActionCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <QuickActionCard>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <LocationIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('dashboard.updateLocation')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('dashboard.keepCustomersInformed')}
                  </Typography>
                  <Button variant="outlined" size="large">
                    {t('dashboard.updateLocation')}
                  </Button>
                </CardContent>
              </QuickActionCard>
            </Grid>
          </Grid>

          {/* Recent Activities for Driver */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <RecentActivities 
                activities={recentActivities} 
                title="Your Recent Activities"
                maxItems={5}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
}